import { useEffect, useMemo, useState } from "react";
import { notificationsClient } from "../apiClient/notificationsClient";
import { ApiError } from "../apiClient/apiRequest";
import { NotifyOneControl } from "../components/NotifyControls";
import { cafeteriaClient } from "../../cafeteria/apiClient/cafeteriaClient";
import type { BroadcastResult } from "../apiClient/notificationsClient";
import type { CafeteriaWallet } from "../../cafeteria/types/cafeteria.types";

export function NotifyEmployeesPage() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BroadcastResult | null>(null);

  const [showSingle, setShowSingle] = useState(false);
  const [wallets, setWallets] = useState<CafeteriaWallet[]>([]);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [walletsError, setWalletsError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    cafeteriaClient
      .getAdminWallets()
      .then(setWallets)
      .catch((err) => setWalletsError(err instanceof Error ? err.message : "Failed to load employees."))
      .finally(() => setWalletsLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return wallets;
    return wallets.filter((w) => w.email.toLowerCase().includes(q));
  }, [wallets, query]);

  const canSend = subject.trim() !== "" && message.trim() !== "";

  async function handleSend() {
    setSending(true);
    setError(null);
    try {
      const res = await notificationsClient.broadcast(subject.trim(), message.trim());
      setResult(res);
      setConfirming(false);
      setSubject("");
      setMessage("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send this notification.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Notify employees</h1>
          <p className="page-sub">Send a one-off email to everyone, or to one employee. Use sparingly.</p>
        </div>
      </header>

      <div className="form-card">
        <h2>Send to all employees</h2>
        <div className="form">
          <div className="form-field">
            <label>Subject</label>
            <input
              type="text"
              placeholder="e.g. Cafeteria closed tomorrow"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
            />
          </div>
          <div className="form-field">
            <label>Message</label>
            <textarea
              placeholder="What do you want employees to know?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={sending}
              rows={8}
            />
          </div>
          {error && <div className="form-banner form-banner--error">{error}</div>}
          {result && (
            <div className="form-banner">
              Sent to {result.sent} of {result.recipientCount} employee(s).
              {result.failed > 0 && ` ${result.failed} failed to send.`}
            </div>
          )}
          <div className="form-actions">
            {confirming ? (
              <div className="cancel-confirm">
                <span>This emails every employee right now. Sure?</span>
                <button type="button" className="btn btn-ghost" onClick={() => setConfirming(false)} disabled={sending}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleSend} disabled={sending}>
                  {sending ? "Sending…" : "Yes, send"}
                </button>
              </div>
            ) : (
              <button type="button" className="btn btn-primary" onClick={() => setConfirming(true)} disabled={!canSend}>
                Send to all employees
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="admin-section-heading-row">
        <h2 className="admin-section-heading">Send to a single employee</h2>
        <button type="button" className="btn btn-ghost" onClick={() => setShowSingle((s) => !s)}>
          {showSingle ? "Hide" : "Show"}
        </button>
      </div>
      {showSingle && (
        <>
          <input
            type="text"
            className="coach-search"
            placeholder="Search by email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search employees by email"
          />
          {walletsError ? (
            <div className="state-panel">
              <p>{walletsError}</p>
            </div>
          ) : walletsLoading ? (
            <div className="state-panel">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="state-panel">
              <p>No employees match.</p>
            </div>
          ) : (
            <ul className="about-admin-list">
              {filtered.map((w) => (
                <li key={w.id} className="about-admin-row">
                  <div className="cafeteria-wallet-row-summary">
                    <span>{w.email}</span>
                  </div>
                  <NotifyOneControl email={w.email} subjectDefault="" messageDefault="" />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
